import { useMemo } from 'react';

import { convertGroupKeyToIndex, createItemKeySet } from '../helpers';
import type { GenericItem, GetItemKey } from '../types';
import { ItemsProp, InputItemGroup } from '../types';

const useGroupFilter = <TItem extends GenericItem>({
  items,
  maybeGroupedItems,
  selectedGroupKey,
  getItemKey,
}: {
  items: TItem[];
  maybeGroupedItems: ItemsProp<TItem>;
  selectedGroupKey?: string;
  getItemKey?: GetItemKey<TItem>;
}): {
  filteredGroupItems: TItem[];
  showGroupHeaders: boolean;
} => {
  const hasPinnedGroups = useMemo(() => {
    if (!Array.isArray(maybeGroupedItems)) {
      return false;
    }
    return (maybeGroupedItems as InputItemGroup<TItem>[]).some((g) => g.pinned);
  }, [maybeGroupedItems]);

  const pinnedItems = useMemo(() => {
    if (!Array.isArray(maybeGroupedItems)) {
      return [];
    }
    const pinnedGroups = (maybeGroupedItems as InputItemGroup<TItem>[]).filter(
      (g) => g.pinned,
    );
    return pinnedGroups.flatMap((g) => g.items ?? []);
  }, [maybeGroupedItems]);

  const filteredGroupItems = useMemo(() => {
    const groupIndex = convertGroupKeyToIndex(selectedGroupKey || '');

    if (groupIndex === null || groupIndex === undefined || groupIndex < 0) {
      return items;
    }

    const group = maybeGroupedItems[groupIndex] as InputItemGroup<TItem>;

    if (getItemKey) {
      const pinnedKeySet = createItemKeySet(pinnedItems, getItemKey);
      const groupKeySet = createItemKeySet(group.items || [], getItemKey);
      const consumedPinnedKeys = new Set<string>();

      return items.filter((item) => {
        const key = getItemKey(item);
        if (pinnedKeySet.has(key) && !consumedPinnedKeys.has(key)) {
          consumedPinnedKeys.add(key);
          return true;
        }
        return groupKeySet.has(key);
      });
    }

    const pinnedRefSet = new Set(pinnedItems);
    const groupRefSet = new Set(group.items || []);
    const consumedPinned = new Set<TItem>();

    return items.filter((item) => {
      if (pinnedRefSet.has(item) && !consumedPinned.has(item)) {
        consumedPinned.add(item);
        return true;
      }
      return groupRefSet.has(item);
    });
  }, [maybeGroupedItems, selectedGroupKey, items, getItemKey, pinnedItems]);

  return {
    filteredGroupItems,
    showGroupHeaders: !selectedGroupKey || hasPinnedGroups,
  };
};
export default useGroupFilter;
